import React, { useState } from 'react'
import { Card } from 'react-bootstrap'
import { useI18n } from '../../../Utils/_helpers/useI18n'
import { SearchBar, PromoActionHeader, CreatePromoActionModal } from './components'
import { usePromoActions } from './hooks/usePromoActions'
import { PromoAction } from '../../../../services/cellcast.v2.types'
import DataTable, { Column } from '../../../Components/shared/data-table/table'
import { KTSVG } from '../../../../_metronic/helpers'
import moment from 'moment'
import { GridSortModel } from '../../../Components/shared/data-table/pagination.types'

const PromoActionList: React.FC = () => {
  const { t } = useI18n()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedPromoAction, setSelectedPromoAction] = useState<PromoAction | null>(null)
  const [sortData, setSortData] = useState<GridSortModel | undefined>()
  const [currentPage, setCurrentPage] = useState(0) // ReactPaginate uses 0-based index
  const [pageSize, setPageSize] = useState(10)

  const {
    promoData,
    total,
    isLoading,
    searchTerm,
    handleSearch,
    fetchPromoActions,
    isAdmin,
  } = usePromoActions()

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value)
  }

  const handleEdit = (item: PromoAction) => {
    setSelectedPromoAction(item)
    setShowCreateModal(true)
  }

  const handleCloseModal = () => {
    setShowCreateModal(false)
    setSelectedPromoAction(null)
  }

  const handleCreateNew = () => {
    setSelectedPromoAction(null)
    setShowCreateModal(true)
  }

  const handleChangeSizeData = (size: number) => {
    setPageSize(size)
    setCurrentPage(0)
    fetchPromoActions({ limit: size, page: 1 })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    const apiPage = page + 1 // Convert from 0-based to 1-based for API
    fetchPromoActions({
      page: apiPage,
      limit: pageSize,
      ...(sortData && {
        sortBy: sortData.field,
        sortOrder: sortData.sort === 'asc' ? 'asc' : 'desc'
      })
    })
  }

  const handleSortData = (sortModel: GridSortModel) => {
    setSortData(sortModel)
    const sortOrder = sortModel.sort === 'asc' ? 'asc' : 'desc'
    const sortBy = sortModel.field
    const apiPage = currentPage + 1 // Convert from 0-based to 1-based for API
    fetchPromoActions({
      sortOrder: sortOrder,
      sortBy: sortBy,
      page: apiPage,
      limit: pageSize
    })
  }

  const tableColumns: Column<PromoAction>[] = [
    {
      id: 'name',
      label: t('PROMO_ACTION.LIST.NAME'),
      sortable: true,
      width: 200,
    },
    ...(!isAdmin
      ? [
        {
          id: 'rawContactCount',
          label: t('PROMO_ACTION.LIST.RAW_CONTACT_COUNT'),
          sortable: false,
          width: 150,
          Cell: (item: PromoAction) => <div className='text-center'>{item.rawContactCount || 0}</div>,
        },
      ]
      : []),
    ...(isAdmin
      ? [
        {
          id: 'industry',
          label: t('PROMO_ACTION.LIST.INDUSTRY'),
          sortable: false,
          width: 150,
        },
        {
          id: 'affiliate',
          label: t('PROMO_ACTION.LIST.AFFILIATE'),
          sortable: false,
          width: 150,
        },
      ]
      : []),
    {
      id: 'createdAt',
      label: t('PROMO_ACTION.LIST.CREATED_AT'),
      sortable: false,
      width: 150,
      Cell: (item: PromoAction) => moment(item.createdAt).format('DD/MM/YYYY'),
    },
    {
      id: 'updatedAt',
      label: t('PROMO_ACTION.LIST.UPDATED_AT'),
      sortable: false,
      width: 150,
      Cell: (item: PromoAction) => moment(item.updatedAt).format('DD/MM/YYYY'),
    },
    {
      id: 'actions',
      label: t('PROMO_ACTION.LIST.ACTIONS'),
      width: 100,
      Cell: (item: PromoAction) => (
        <div className='d-flex justify-content-center'>
          <button
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            onClick={() => handleEdit(item)}
          >
            <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
          </button>
        </div>
      ),
    },
  ]

  return (
    <>
      <Card className='card-flush'>
        <PromoActionHeader
          columns={tableColumns}
          data={promoData || []}
          onCreateNew={handleCreateNew}
        />
        <SearchBar value={searchTerm} onChange={handleSearchChange} isLoading={isLoading} />
        <Card.Body className='pt-2'>
          <DataTable
            items={promoData || []}
            isLoading={isLoading}
            columns={tableColumns}
            sortData={sortData}
            pageSize={pageSize}
            totalData={total}
            handleChangePage={handlePageChange}
            handleChangSizeData={handleChangeSizeData}
            handleSortedChange={handleSortData}
            page={currentPage}
          />
        </Card.Body>
        {/* <PromoActionFooter columns={tableColumns} data={promoData || []} /> */}
      </Card>

      <CreatePromoActionModal
        isAdmin={isAdmin}
        show={showCreateModal}
        handleClose={handleCloseModal}
        promoAction={selectedPromoAction}
        onSuccess={() => {
          handleCloseModal()
          const apiPage = currentPage + 1 // Convert from 0-based to 1-based for API
          fetchPromoActions({
            page: apiPage,
            limit: pageSize,
            forceRefresh: true,
            ...(sortData && {
              sortBy: sortData.field,
              sortOrder: sortData.sort === 'asc' ? 'asc' : 'desc'
            })
          })
        }}
      />
    </>
  )
}

export default PromoActionList
